import { Color } from "@repo/app-types/constants/colors";
import {
  computeCriteriaOutcomeImpactLevel,
  computeCriteriaOutcomeImpactScore,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { getOutcomeImpactCopy } from "@repo/app-types/helpers/outcomeImpactCopy";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

interface OutcomePotentialCardProps {
  readonly rank: number;
  readonly totalCriteria: number;
  readonly auc: number;
  readonly netConversions: number;
  readonly averageRevenuePerOpp?: number;
  /** Noun form of the outcome (e.g., "enrollment", "win") */
  readonly outcomeNoun: string | undefined;
  /** Per-playbook plural noun override (see `getOutcomePluralNoun`). */
  readonly outcomePluralNoun?: string | undefined;
  /** Whether this is the revenue outcome (see `isRevenueOutcomeDefinition`). */
  readonly isRevenueOutcome?: boolean | undefined;
}

const getRankStyleParameters = (
  rank: number,
  totalCriteria: number,
): { color: PillColor; textColor: string } => {
  if (rank === 1) {
    return {
      color: Color.Green,
      textColor: "text-green-700",
    };
  }
  if (rank <= Math.ceil(totalCriteria * 0.2)) {
    return {
      color: Color.Green,
      textColor: "text-success",
    };
  }
  if (rank <= Math.ceil(totalCriteria * 0.4)) {
    return {
      color: Color.Lime,
      textColor: "text-lime-600",
    };
  }
  return {
    color: Color.Gray,
    textColor: "text-muted-foreground",
  };
};

const getImpactStyleParameters = (
  impactScore: number,
): { level: string; textColor: string } => {
  const level = computeCriteriaOutcomeImpactLevel(impactScore);

  switch (level) {
    case "very_high":
      return {
        level: "Very High",
        textColor: "text-green-700",
      };
    case "high":
      return {
        level: "High",
        textColor: "text-success",
      };
    case "medium":
      return {
        level: "Medium",
        textColor: "text-lime-600",
      };
    case "low":
      return {
        level: "Low",
        textColor: "text-gray-600",
      };
    case "very_low":
    default:
      return {
        level: "Very Low",
        textColor: "text-muted-foreground",
      };
  }
};

export const OutcomePotentialCard: FC<OutcomePotentialCardProps> = ({
  rank,
  totalCriteria,
  auc,
  netConversions,
  averageRevenuePerOpp,
  outcomeNoun,
  outcomePluralNoun,
  isRevenueOutcome,
}) => {
  const rankStyle = getRankStyleParameters(rank, totalCriteria);
  const impactStyle = getImpactStyleParameters(
    computeCriteriaOutcomeImpactScore(auc),
  );
  const impactCopy = getOutcomeImpactCopy({
    netConversions,
    averageRevenuePerOpp,
    outcomeNoun,
    outcomePluralNoun,
    isRevenueOutcome,
  });
  const noun = impactCopy.noun;
  const pluralNoun = impactCopy.pluralNoun;
  const capitalizedNoun = noun.charAt(0).toUpperCase() + noun.slice(1);

  return (
    <div className="rounded-md bg-white px-4 pt-3 pb-4 shadow-sm ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-x-8">
        <Label size={Size.SMALL} className="mb-2">
          {capitalizedNoun} potential
        </Label>

        <div className="mb-2 flex items-center gap-2">
          <Pill color={rankStyle.color} size={Size.SMALL} label={`#${rank}`} />
          <span className={twMerge("text-xs font-medium", rankStyle.textColor)}>
            rank of {totalCriteria} criteria
          </span>
        </div>
      </div>
      <p className="text-xs text-gray-600">
        {netConversions > 0 ? (
          <>
            This criteria has a{" "}
            <span
              className={twMerge(
                "font-semibold lowercase",
                impactStyle.textColor,
              )}
            >
              {impactStyle.level}
            </span>{" "}
            impact on {noun}. Coaching on this criterion can{" "}
            <span className="font-semibold text-success">
              {impactCopy.sentence}
            </span>
            .
          </>
        ) : (
          <>
            Coaching your team on this criterion is unlikely to significantly
            increase {pluralNoun}.
          </>
        )}
      </p>
    </div>
  );
};
